import { gsap } from "gsap";
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { IDimOrientation, LayoutDimData, ResizeHelper } from "../../../core/utils/ResizeHelper";
import { Helper } from "../../../core/utils/Helper";
import { TranslationConfig } from "../../config/translate/TranslationConfig";
import { TextStyleConfig } from "../../config/translate/TextStyleConfig";
import { TextStyleOptions } from "../../../core/setting/text/IText";
import { UniversalPlayer, UniPlayer } from '../../../core/displayobject/UniversalPlayer';
import { ISequencePlay, Sequence } from "../../../core/displayobject/Sequence";
import { SpinePlayConfig } from "../../../core/displayobject/SpinePlayer";
import { GameSetting } from '../../../core/setting/GameSetting';
import { MathHelper } from "../../../core/utils/MathHelper";
import { SanQuentin2 } from "../../SanQuentin2";
import { BLEND_MODES } from "pixi.js";

export class BigWinPosterState {

    public static readonly BIG_WIN_P = "Wins_Placeholder/BigWin";
    public static readonly BIG_WIN = "Big_Win";

    public static bigWinLvl(lvl:number):number{
        return MathHelper.clamp(lvl+1,1,6);
    }

    public static bigWinSpinePath(lvl:number):string{
        if((GameSetting.JuriGFXCensorSettings || GameSetting.JuriGFXUSCensorSettings) && this.bigWinLvl(lvl) == 5){
            return "bw5_blurr";
        }else
            return "bw" + this.bigWinLvl(lvl);
    }
}

export class BigWinPoster extends PIXI.Container implements StageLayer {

    public get dim():LayoutDimData{ return ResizeHelper.calcDim(this.PosterDim); };

    private readonly WIDTH:number = 3600;
    private readonly HEIGHT:number = 3600;

    protected readonly PosterDim:IDimOrientation = {
        landscape :{
            scale:      new PIXI.Point( 1.35, 1.35),
            position:   new PIXI.Point( 360, 320),
        },
        portrait :{
            scale:      new PIXI.Point( 1, 1),
            position:   new PIXI.Point( 360, 220)
        }
    };
    
    private player:UniversalPlayer;
    private postObj:UniPlayer;
    private blackGraphics:PIXI.Graphics;
    private gradientSupported:boolean = false;
    private gradientOverlay:PIXI.Sprite;

    private isCap:boolean = false;

    protected totalWinText:PIXI.Text;

    private timeline:gsap.core.Timeline;
    private timelineBlack:gsap.core.Timeline;

    private splashSeq1:Sequence;
    private splashSeq2:Sequence;

    private boom1:Sequence;
    private boom2:Sequence;

    constructor(){ 
        super();
        this.initAnimations(); 
    };

    protected initAnimations():void{

        this.createGraphics();
        
        // try{
        //     this.gradientOverlay = this.addChild(new PIXI.Sprite(this.createGradTexture(this.gradientColorLvl(0))));
        //     this.gradientOverlay.anchor.set(0.5,0.5);
        //     this.gradientOverlay.rotation = MathHelper.degreeToAngle(90);
        //     this.gradientOverlay.alpha = 1;
        // }
        // catch{
        //     this.gradientSupported = false;
        // }

        this.player = new UniversalPlayer();
        this.player.load(BigWinPosterState.BIG_WIN);

        this.player.uniPlayers.forEach(p => {
            this.addChild(p.obj);
            p.obj.alpha = 0;
        });

        this.boom1 = this.addChild(new Sequence("explosion"))
        this.boom1.anchor.set(0.5);
        this.boom1.position.set(-260,260);
        this.boom2 = this.addChild(new Sequence("explosion"))
        this.boom2.anchor.set(0.5);
        this.boom2.position.set(260,260);

        this.boom1.blendMode = this.boom2.blendMode = BLEND_MODES.ADD;
        this.boom1.visible = this.boom2.visible = false;

        this.totalWinText = this.createText(GameSetting.translate(TranslationConfig.TOTAL_WIN), TextStyleConfig.TOTAL_WIN);
        this.onResize();
    }

    public gradientColorLvl(lvl:number):string{
        switch(lvl){
            case 1:
                return "rgb(9,141,55,0.6)";
            case 2:
                return "rgb(0,133,148,0.6)";
            case 3:
                return "rgb(7,41,139,0.6)";
            case 4:
                return "rgb(142,8,133,0.6)";
            case 5:
                return "rgb(141,7,14,0.6)";
            default:
                return "rgb(0,0,0,0.6)"
        }
    }

    public kaboom(lvl:number){
        this.boom1.scale.set(1+ lvl*0.1);
        this.boom1.visible =this.boom2.visible = true;
        this.boom1.alpha =this.boom2.alpha = 1;
        this.tlKaboom = Helper.setTimeline(this.tlKaboom);
        this.tlKaboom.add(this.boom1.play(),0);
        this.tlKaboom.add(this.boom2.play(),0);

        this.tlKaboom.to([this.boom1,this.boom2],{duration:this.tlKaboom.duration()*0.3,alpha:0},"-="+this.tlKaboom.duration()*0.3)
    }

    private tlKaboom:gsap.core.Timeline;
    public kaboomLoop(play:boolean){

        this.tlKaboom = Helper.setTimeline(this.tlKaboom);

        if(play){
            const ranTrigg = Math.random() > 0.5;
            const first = ranTrigg ? this.boom1 : this.boom2;
            const second = ranTrigg ? this.boom2 : this.boom1;
    
            this.tlKaboom.add(() => {
                first.visible = true;
                first.alpha = 1;
                first.play({repeat:-1})
            })
            this.tlKaboom.add(() => {
                second.visible = true;
                second.alpha = 1;
                second.play({repeat:-1});
            }, 0.15+ (Math.random() * 0.15) )
        }else{
            this.tlKaboom.to([this.boom1 , this.boom2],{alpha:0, duration:0.5},0);
            this.tlKaboom.add(() => {
                this.boom1.stop()
                this.boom2.stop()
            })
        }
    }

    protected createGradTexture(color:string):PIXI.Texture {
        // adjust it if somehow you need better quality for very very big images
        const quality = 126;
        const canvas = document.createElement('canvas');
        canvas.width = quality;
        canvas.height = 1;
    
        const ctx = canvas.getContext('2d');
    
        // use canvas2d API to create gradient
        const grd = ctx!.createLinearGradient(0, 0, quality, 0);
        grd.addColorStop(0, color);
        grd.addColorStop(0.75, 'black');
    
        ctx!.fillStyle = grd;
        ctx!.fillRect(0, 0, quality, 1);
    
        return PIXI.Texture.from(canvas);
    }

    public setCap(val:boolean):void {
        this.isCap = val;
        this.onResize();
    }

    private createText(text:string, style:TextStyleOptions):PIXI.Text {
        const textField = new PIXI.Text(text, style);
        textField.style.align = "center";
        textField.anchor.set(0.5, 0.5);
        textField.alpha = 0;
        this.addChild(textField);
        return textField;
    }

    public play(state:string, conf?:ISequencePlay | SpinePlayConfig, fadeIn?:number,gradientLvl?:number):gsap.core.Timeline{

        this.timeline = Helper.setTimeline(this.timeline);
        if(this.postObj){
            this.postObj.stop(true);
            this.postObj.obj.visible = false;
        }

        this.postObj = this.player.get(state);
        this.timeline.add(this.postObj.play(conf));
        this.timeline.add(() => { this.postObj.obj.visible = true; },0)

        if(this.gradientSupported){
            this.gradientOverlay.texture = this.createGradTexture(this.gradientColorLvl(BigWinPosterState.bigWinLvl(gradientLvl ? gradientLvl : 0)));
            if(fadeIn != undefined)
                this.timeline.fromTo(this.gradientOverlay,{alpha:0}, {duration:fadeIn, alpha:1},0)
            else
                this.gradientOverlay.alpha = 1;
        }else{
            if(fadeIn != undefined)
                this.timeline.fromTo(this.blackGraphics,{alpha:0},{duration:fadeIn, alpha:0.6},0)
            else
                this.blackGraphics.alpha = 0.4;
        }

        if(fadeIn != undefined)
            this.timeline.fromTo(this.postObj.obj,{alpha:0}, {duration:fadeIn, alpha:1},0)
        else 
            this.postObj.obj.alpha = 1;

        return this.timeline;
    }

    splash(){
        if(!this.splashSeq1 || !this.splashSeq2){
            this.splashSeq1 = this.addChild(createSeq("Win_Xplod_A"));
            this.splashSeq2 = this.addChild(createSeq("Win_Xplod_B"));
        }

        this.splashSeq1.play({
            onStart:() => {this.splashSeq1.alpha = 1;}, 
            onCompleteCallback:() => { this.splashSeq1.alpha = 0;}
        });

        this.splashSeq2.play({
            onStart:() => {this.splashSeq2.alpha = 1;}, 
            onCompleteCallback:() => { this.splashSeq2.alpha = 0;}
        });

        function createSeq(path:string){
            const seq = new Sequence(SanQuentin2.assetLoader.getTextures(path))
            seq.blendMode = BLEND_MODES.ADD;
            seq.anchor.set(0.5);
            seq.position.y = ResizeHelper.isLandscape ? 160 : 235;
            seq.scale.set(1.75);
            return seq;
        }
    }

    public stop(fadeout:number = 0.2,playEnd?:boolean):gsap.core.Timeline{
        if(!this.postObj) return Helper.setTimeline()
        this.timeline = Helper.setTimeline(this.timeline, {
            onUpdate:() => {
                if(this.postObj.obj)
                    this.postObj.obj.alpha = 1-this.timeline!.progress();
            },
            onComplete:() => {
                this.postObj.stop(playEnd);
                if(this.postObj.obj)
                    this.postObj.obj.alpha = 0;
            }
        });

        if(this.gradientSupported && this.gradientOverlay.alpha > 0) this.timeline.to(this.gradientOverlay, {duration:fadeout, alpha:0},0)
        this.timeline.to(this.blackGraphics,{duration:fadeout,alpha:0},0)
        
        return this.timeline;
    }

    public setBlackScreenAlpha(to:number,duration:number = 0.3):gsap.core.Timeline{
        this.timelineBlack = Helper.setTimeline(this.timelineBlack);
        this.timelineBlack.to(this.blackGraphics,duration,{alpha:to});
        return this.timelineBlack;
    }

    private createGraphics():void{
        this.blackGraphics = new PIXI.Graphics();
        this.blackGraphics.alpha = 0;
        this.blackGraphics.interactive = false;
        this.blackGraphics.buttonMode = false;

        this.blackGraphics.beginFill( 0x000000, 1);
        this.blackGraphics.drawRect(-this.WIDTH/2,-this.HEIGHT/2,this.WIDTH, this.HEIGHT);
        this.blackGraphics.endFill();

        this.addChild(this.blackGraphics);
    }

    public onResize(){
        if(this.blackGraphics === undefined) this.initAnimations();// return;
        this.totalWinText.y = ResizeHelper.isLandscape? -225 : -150;

        this.position = this.dim.position;  

        if(!this.isCap)
            this.scale = this.dim.scale;
        else{
            this.scale.set(1);
        }

        if(this.splashSeq1){
            this.splashSeq1.position.y = this.splashSeq2.position.y = ResizeHelper.isLandscape ? 160 : 235;
        }
    }
}